import React, { useState } from 'react';

/* ============================================================================
   MM2 MARKA — TEK KAYNAK
   ----------------------------------------------------------------------------
   MM2'yi temsil eden her görsel öğe buradan gelir. Daha önce her ekran kendi
   çözümünü uyduruyordu: cüzdan kartında yeşil daire içinde "MM" yazan bir
   rozet, para simgesinde çizilmiş bir madeni para, oyun sayfasında hiçbir şey.
   Aynı marka üç ekranda üç farklı göründüğünde kullanıcı bunu "site derme
   çatma" diye okur.

   ⚠️ TEK DOSYA: logo değişirse bir yol güncellenir, on dosya değil.
============================================================================ */

/* Görselin yolu. public/ kökünde durur (index.html ile aynı seviye). */
export const MM2_LOGO_SRC = '/mm2logo.png';

/**
 * MM2 logosu.
 *
 * ⚠️ HATA DURUMU SESSİZ. Dosya bir gün silinirse tarayıcının kırık görsel
 *    ikonu ekrana düşer ve bu, kullanıcıya "site bozuk" dedirtir. Yüklenmezse
 *    bileşen kendini gizler; çevresindeki düzen zaten ayakta durur.
 */
export const Mm2Logo = ({ size = 20, className = '', style, alt = 'Murder Mystery 2' }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={MM2_LOGO_SRC}
      alt={alt}
      width={size}
      height={size}
      draggable={false}
      onError={() => setFailed(true)}
      className={`object-contain select-none shrink-0 ${className}`}
      style={{ width: size, height: size, ...style }}
    />
  );
};

/**
 * Filigran logo — kartların arkasında duran büyük, soluk kopya.
 *
 * ⚠️ `pointer-events-none` ŞART: kartın kendisi bir buton ve filigran
 *    tıklamayı yutarsa buton çalışmaz.
 */
export const Mm2Watermark = ({ size = 128, opacity = 0.06, className = '', style }) => (
  <div
    aria-hidden
    className={`pointer-events-none absolute select-none ${className}`}
    style={{ opacity, ...style }}
  >
    <Mm2Logo size={size} alt="" />
  </div>
);

/**
 * Geri butonu — TEK KAYNAK.
 *
 * ⚠️ Üç ekranda üç ayrı kopyası vardı ve üçü farklı boyuttaydı. Buradaki
 *    hâli kare bir dokunma hedefi (36px): başlık satırının yanında
 *    hizalanabilsin diye. Metin YOK — ok işareti evrensel ve dar bir
 *    modalda her piksel başlığın hakkı.
 */
export const BackButton = ({ onClick, label = 'Back', testId }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label}
    data-testid={testId}
    className="mm-focus w-9 h-9 shrink-0 grid place-items-center rounded-lg
               bg-[#15171a] border border-[#2A2D31] text-zinc-400
               hover:text-white hover:border-[#3a3d43] transition-colors">
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  </button>
);

/* ============================================================================
   YATIRMA MODU DEFTERİ
   ----------------------------------------------------------------------------
   SORUN: sitede İKİ AYRI yatırma var ve ikisi de aynı `/game/mm2/session`
   ucundan görünüyor:

     1) CÜZDAN yatırması   (mode: 'balance') → eşya verirsin, MM Coin alırsın.
        Kasino oyunlarında harcanır. Eşya envanterine HİÇBİR ŞEY girmez.
     2) COINFLIP yatırması (mode: 'items')   → eşya verirsin, eşya olarak
        envanterine düşer. MM2 coinflip'te bahis olarak oynanır.

   Oturum ucu `type: "deposit"` diyor ama HANGİ yatırma olduğunu söylemiyor.
   Sonuç: kullanıcı cüzdandan MM Coin yatırmaya başlayıp coinflip sayfasına
   geçtiğinde sayfa "Deposit in progress" penceresini üstüne açıyor ve
   coinflip'in kendi yatırma akışını kilitliyordu — oysa o, tamamen başka
   bir işlem.

   ÇÖZÜM: modu BAŞLATAN taraf yazar. `/game/mm2/deposit` yanıtı `mode`
   döndürüyor; işlemi kim başlattıysa trade_id ile birlikte buraya kaydeder,
   okuyan taraf da hangi rayda olduğunu bilir.

   ⚠️ localStorage BİLEREK: sekmeler arası paylaşılır (kullanıcı cüzdanı bir
      sekmede açıp coinflip'i başka sekmede açabilir) ve yenilemede durur.
   ⚠️ GÜVENLİK SINIRI: bu defter yalnızca GÖRÜNÜM kararıdır. Paranın nereye
      yazılacağına sunucu karar verir ve modu kendi belgesine mühürler;
      buradaki kaydı kurcalamak hiçbir bakiyeyi değiştirmez.
============================================================================ */

const LEDGER_KEY = 'mm2:deposit-modes';
const LEDGER_TTL_MS = 24 * 60 * 60 * 1000;   /* bir gün sonra kendini temizler */

const readLedger = () => {
  try {
    const raw = window.localStorage.getItem(LEDGER_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    /* Kota dolu, gizli mod, bozuk JSON — hiçbiri arayüzü durdurmamalı. */
    return {};
  }
};

const writeLedger = (obj) => {
  try { window.localStorage.setItem(LEDGER_KEY, JSON.stringify(obj)); }
  catch { /* yazamadıysak varsayılan davranışa düşeriz, çökmeyiz */ }
};

/** İşlem başlatılırken çağrılır: bu trade hangi raya ait? */
export const rememberDepositMode = (tradeId, mode) => {
  if (!tradeId || !mode) return;
  const led = readLedger();
  const now = Date.now();
  /* Aynı anda eski kayıtları da süz — ayrı bir temizlik işine gerek kalmasın. */
  const next = {};
  Object.entries(led).forEach(([k, v]) => {
    if (v && now - (v.at || 0) < LEDGER_TTL_MS) next[k] = v;
  });
  next[tradeId] = { mode, at: now };
  writeLedger(next);
};

/**
 * Bir işlemin rayını söyler.
 * @returns 'balance' | 'items' | 'house' | null  (null = bilinmiyor)
 *
 * ⚠️ BİLİNMİYOR ≠ 'items'. Kullanıcı belleği temizlemiş ya da işlemi başka
 *    cihazda başlatmış olabilir. Çağıran taraf null'ı KENDİ güvenli tarafına
 *    yorumlamalı — burada tahmin yürütmüyoruz.
 */
export const depositModeOf = (tradeId) => {
  if (!tradeId) return null;
  const rec = readLedger()[tradeId];
  if (!rec) return null;
  if (Date.now() - (rec.at || 0) > LEDGER_TTL_MS) return null;
  return rec.mode || null;
};

/**
 * Bir oturum CÜZDAN yatırması mı (eşya → MM Coin)?
 *
 * Oturum birden çok bacaklı olabilir; hepsi aynı gruptan geldiği için
 * herhangi birinin kaydı yeterli.
 */
export const isWalletDepositSession = (session) => {
  if (!session || session.type !== 'deposit') return false;
  const ids = [
    session.group_id,
    session.active_step?.trade_id,
    ...(session.steps || []).map((s) => s.trade_id),
  ].filter(Boolean);
  return ids.some((id) => depositModeOf(id) === 'balance');
};

export default Mm2Logo;
